import React, { useState, useEffect, useContext, useCallback } from "react";
import { Button, Table, Space, Dropdown, notification, Input, Select } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  DownOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import TemplatePointsForm from "../components/Forms/TemplatePointsForm";
import axios from "axios";
import "moment-timezone";
import GlobalContext from "../utils/GlobalContext";
import { v4 as uuidv4 } from "uuid";
import MessageDialog from "../components/MessageDialog";
import LoadingModal from "../utils/LoadingModal";
import formatTimestamp from "../utils/FormatTimestamp";

const PAGE_SIZE = 15;
const { Option } = Select;

const TemplatePointsTable = () => {
  const globals = useContext(GlobalContext);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTemplatePointsForm, setShowTemplatePointsForm] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentTemplatePoint, setCurrentTemplatePoint] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: PAGE_SIZE,
  });
  const [messageVisible, setMessageVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [meeting_types, setMeetingTypes] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [meetingTypesId, setMeetingTypesId] = useState([]);
  const [departmentsId, setDepartmentsId] = useState([]);

  const columns = [
    // {
    //   title: "ID",
    //   dataIndex: "id",
    //   key: "id",
    //   width: 50,
    // },
    {
      title: "Nombre del punto",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Departamento",
      dataIndex: ["department", "name"],
      key: "department",
      render: (_, record) => {
        return record?.department ? record?.department.name : "<TODOS>";
      }
    },
    {
      title: "Tipo de reunión",
      dataIndex: ["meeting_type", "name"],
      key: "meeting_type",
      render: (_, record) => {
        return record?.meeting_type ? record?.meeting_type.name : "<TODOS>";
      }
    },
    {
      title: "Tipo de punto",
      dataIndex: ["point_type", "name"],
      key: "point_type",
    },
    {
      title: "Clasificación",
      dataIndex: ["classification", "name"],
      key: "classification",
    },
    // {
    //   title: "Descripción",
    //   dataIndex: "description",
    //   key: "description",
    // },
    {
      title: "Duración",
      dataIndex: "duration",
      key: "duration",
      render: (text) => {
        return (text) ? `${text} min`: `0 min`;
      },
    },
    {
      title: "Estatus",
      dataIndex: "status",
      key: "status",
      render: (text) => {
        // mostrar icono y texto basado en el estado
        return text === "ACTIVO" || text === "ACTIVE" ? (
          <span style={{ color: "green" }}>
            <CheckCircleOutlined style={{ marginRight: 8 }} />
            Activo
          </span>
        ) : (
          <span style={{ color: "red" }}>
            <CloseCircleOutlined style={{ marginRight: 8 }} />
            Inactivo
          </span>
        );
      },
    },
    {
      title: "Última actualización",
      dataIndex: "UpdatedAt",
      render: (_, record) => (
        <span>
          {record?.updatedAt ? formatTimestamp(record?.updatedAt) : "N/A"}
        </span>
      ),
    },
    {
      title: "Opciones",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Space size="middle">
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "1",
                  label: (record?.status === "ACTIVO" || record?.status === "ACTIVE") ? "Inactivar" : "Activar",
                  icon: (record?.status === "ACTIVO" || record?.status === "ACTIVE") ? <CloseCircleOutlined /> : <CheckCircleOutlined />,
                  onClick: () => handleActionClick("status", record),
                },
                {
                  key: "2",
                  label: "Editar",
                  icon: <EditOutlined />,
                  onClick: () => handleActionClick("edit", record),
                },
                {
                  key: "3",
                  label: "Eliminar",
                  icon: <DeleteOutlined />,
                  onClick: () => handleActionClick("delete", record),
                },
              ],
            }}
          >
            <Button icon={<DownOutlined />} shape="circle" />
          </Dropdown>
        </Space>
      ),
    },
  ];

  const fetchMeetingTypes = useCallback(async () => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 100));
      const response = await axios.get(
        `${globals.SERVICE_API_DB}/v1/list-meeting-types`,
        {
          headers: {
            "x-token": globals.token,
          },
        }
      );
      setMeetingTypes(response?.data?.data);
    } catch (error) {
      console.error("Error fetching meeting types", error.response);
      notification.error({
        message: "Error obteniendo tipos de reunión",
        placement: "bottomRight",
      });
    }
    // setLoadingClients(false);
  }, [globals.SERVICE_API_DB, globals.token]);

  const fetchDepartments = useCallback(async () => {
    try {
      // setLoadingClients(true);
      await new Promise((resolve) => setTimeout(resolve, 100));
      const response = await axios.get(
        `${globals.SERVICE_API_DB}/v1/list-departments`,
        {
          headers: {
            "x-token": globals.token,
          },
        }
      );
      setDepartments(response?.data?.data);
    } catch (error) {
      console.error("Error fetching departments:", error.response);
      notification.error({
        message: "Error obteniendo departamentos",
        placement: "bottomRight",
      });
    }
    // setLoadingClients(false);
  }, [globals.SERVICE_API_DB, globals.token]);

  const fetchData = useCallback(
    async (newPagination, search, meetingTypesId, departmentsId) => {
      let page = 1;
      let pageSize = PAGE_SIZE;
      if (newPagination) {
        page = newPagination.current;
        pageSize = newPagination.pageSize;
      }
      try {
        setLoading(true);
        await new Promise((resolve) => setTimeout(resolve, 100));
        const response = await axios.get(
          `${globals.SERVICE_API_DB}/v1/list-template-point`,
          {
            headers: {
              "x-token": globals.token,
            },
            params: {
              page,
              limit: pageSize,
              search,
              meetingTypesId,
              departmentsId,
              onlyActive: false,
            },
          }
        );
        setData(response?.data?.data?.rows);
        if (newPagination) {
          setPagination({
            ...newPagination,
            total: response?.data?.data?.count,
          });
        } else {
          setPagination({
            ...pagination,
            total: response?.data?.data?.count,
          });
        }
        notification.success({
          message: "Información obtenida exitosamente!",
          placement: "bottomRight",
          duration: 2,
        });
      } catch (error) {
        console.error("Error fetching data:", error.response);
        if (error?.response?.data.status === -2){
          notification.warning({
            message: "No hay información disponible!",
            placement: "bottomRight",
          });
          setData([]);
        }else{
          notification.error({
            message: "Error obteniendo información!",
            placement: "bottomRight",
          });
        }
      }
      setLoading(false);
    },
    // eslint-disable-next-line
    [globals.SERVICE_SERVICE_API_DB]
  );

  useEffect(() => {
    fetchMeetingTypes();
    fetchDepartments();
    fetchData(null, null, null, null);
  }, [fetchData, fetchMeetingTypes, fetchDepartments]);

  const handleActionClick = async (key, record) => {
    if (key === "edit") {
      setIsEdit(true);
      setCurrentTemplatePoint(record);
      setShowTemplatePointsForm(true);
    } else if (key === "delete") {
      setCurrentTemplatePoint(record);
      setMessageVisible(true);
    }
    else if (key === "status") {
      try {
        await axios.put(
          `${globals.SERVICE_API_DB}/v1/update-template-point-status/${record.id}`,
          {
            status: record.status === "ACTIVO" || record.status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
          },
          {
            headers: {
              "x-token": globals.token,
            },
          }
        );
        // console.log("pre status", record.status);
        // record.status = record.status === "ACTIVO" || record.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
        // console.log("changing status", record.status);
        // setCurrentTemplatePoint(record);


        const newData = data.map(item => {
          if (item.id === record.id) {
            return {
              ...item,
              status: item.status === "ACTIVO" || item.status === "ACTIVE" ? "INACTIVE" : "ACTIVE"
            };
          }
          return item;
        });
    
        // Actualizar el estado con los nuevos datos
        setData(newData);



        notification.success({
          message: "Estatus actualizado exitosamente!",
          placement: "bottomRight",
          duration: 2,
        });
      } catch (error) {
        console.error("Error updating status:", error.response);
        notification.error({
          message: "Error actualizando estatus!",
          placement: "bottomRight",
        });
      }
    }
  };

  async function handleTableChange(newPagination) {
    fetchData(newPagination, searchText, meetingTypesId, departmentsId);
  }

  const saveTemplatePointForm = async ({ status, newTemplatePoint }) => {
    setShowTemplatePointsForm(false);
    if (isEdit) {
      setIsEdit(false);
      notification.success({
        message: "Punto de plantilla editado exitosamente!",
        placement: "bottomRight",
        duration: 2,
      });
      return;
    }
    if (status === "created") {
      setData([newTemplatePoint, ...data]);
    }

    notification.success({
      message: "Punto de plantilla creado exitosamente!",
      placement: "bottomRight",
      duration: 2,
    });
  };

  const closeTemplatePointForm = () => {
    setShowTemplatePointsForm(false);
    setIsEdit(false);
    setCurrentTemplatePoint(null);
  };

  const deleteTemplatePoint = async () => {
    setMessageVisible(false);
    try {
      await axios.delete(
        `${globals.SERVICE_API_DB}/v1/delete-template-point/${currentTemplatePoint.id}`,
        {
          headers: {
            "x-token": globals.token,
          },
        }
      );
      setData(data.filter((item) => item.id !== currentTemplatePoint.id));
      notification.success({
        message: "Punto de plantilla eliminado!",
        placement: "bottomRight",
        duration: 2,
      });
    } catch (err) {
      console.error(err.response.data);
      notification.error({
        message: "Error eliminando departamento",
        placement: "bottomRight",
      });
      return;
    }
  };

  const handleRefresh = () => {
    fetchData(null, searchText, meetingTypesId, departmentsId);
  };

  const handleSearch = async () => {
    fetchData(null, searchText, meetingTypesId, departmentsId);
  };

  const handleSearchEnterKey = async (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };
  const filterMeetingTypeOption = (input, option) =>
    (option?.children ?? "").toLowerCase().includes(input.toLowerCase());

  const filterDepartmentsOption = (input, option) =>
    (option?.children ?? "").toLowerCase().includes(input.toLowerCase());

  return (
    <div>
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}
      >
        <h2 style={{ marginRight: "16px" }}>Puntos de plantillas</h2>
        <div style={{ flexGrow: 1, borderBottom: "1px solid #ccc" }} />
        <Button
          onClick={() => {
            setIsEdit(false);
            setShowTemplatePointsForm(true);
          }}
          style={{ marginRight: "8px" }}
        >
          Crear
        </Button>
        <Button
          onClick={handleRefresh}
          disabled={loading}
          style={{ marginRight: "8px" }}
        >
          Refrescar
        </Button>
      </div>
      <Input
        allowClear
        placeholder="Ingresa un nombre"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ marginBottom: 16, width: 200 }}
        prefix={<SearchOutlined />}
        onKeyDown={handleSearchEnterKey}
      />
      <Select
        allowClear
        style={{ width: 200, marginLeft: 8 }}
        showSearch
        value={departmentsId}
        onChange={(value) => setDepartmentsId(value)}
        placeholder="Departamentos"
        filterOption={filterDepartmentsOption}
        optionFilterProp="children"
      >
        {departments.map((option) => (
          <Option key={option.id} value={option.id}>
            {option.name}
          </Option>
        ))}
      </Select>
      <Select
        allowClear
        style={{ width: 200, marginLeft: 8 }}
        showSearch
        value={meetingTypesId}
        onChange={(value) => setMeetingTypesId(value)}
        placeholder="Tipo de reunión"
        filterOption={filterMeetingTypeOption}
        optionFilterProp="children"
      >
        {meeting_types.map((option) => (
          <Option key={option.id} value={option.id}>
            {option.name}
          </Option>
        ))}
      </Select>
      
      <Button onClick={handleSearch} style={{ width: 100, marginLeft: "8px" }}>
        Buscar
      </Button>
      <div
        className="site-layout-background h-auto"
        style={{
          padding: 10,
          display: "flex",
          flexDirection: "column",
          margin: 0,
          height: "auto",
          minHeight: "350px",
          overflow: "auto",
          backgroundColor: "#ffffff",
        }}
      >
        <Table
          rowKey={(r) => uuidv4()}
          columns={columns}
          dataSource={data}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ y: "max-content" }}
          size="small"
        />
      </div>
      <LoadingModal open={loading} />

      <TemplatePointsForm
        open={showTemplatePointsForm}
        onFinish={saveTemplatePointForm}
        onCancel={closeTemplatePointForm}
        isEdit={isEdit}
        currentTemplatePoint={currentTemplatePoint}
      />

      <MessageDialog
        visible={messageVisible}
        title="Está seguro de eliminar este punto de plantilla?"
        message="Esta acción no se puede deshacer."
        onOk={deleteTemplatePoint}
        onCancel={() => setMessageVisible(false)}
        confirm={true}
      />
    </div>
  );
};

export default TemplatePointsTable;
